import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, X, Save, Ruler } from "lucide-react";
import { toast } from "sonner";
import { kgToLbs, lbsToKg, cmToFtIn, ftInToCm } from "@/lib/units";
import { MedicationFields } from "@/components/MedicationFields";
import { normalizeAge, ageError, medicationsError, cleanMedications, profileErrorMessage } from "@/lib/profileValidation";

export default function ProfilePage() {
  const [ref, setRef] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState("");
  const [newAllergy, setNewAllergy] = useState("");
  const [newExclude, setNewExclude] = useState("");
  const [newInclude, setNewInclude] = useState("");
  // Local input buffers for imperial fields so users can freely type
  const [lbBuf, setLbBuf] = useState("");
  const [ftBuf, setFtBuf] = useState("");
  const [inBuf, setInBuf] = useState("");

  useEffect(() => {
    Promise.all([api.get("/reference"), api.get("/profile")]).then(([r, p]) => {
      setRef(r.data);
      const initial = p.data || {
        age: 30, sex: "female", height_cm: 168, weight_kg: 70,
        activity_level: "moderate", conditions: [], medications: [], allergies: [],
        custom_allergies: [], excluded_ingredients: [],
        dietary_preferences: [], goals: [], units: "metric", notes: "",
      };
      setForm(initial);
      setLbBuf(String(kgToLbs(initial.weight_kg)));
      const fi = cmToFtIn(initial.height_cm);
      setFtBuf(String(fi.ft));
      setInBuf(String(fi.in));
    });
  }, []);

  if (!ref || !form) return <div className="p-10 text-slate-500">Loading…</div>;

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => {
    const arr = form[key];
    if (!arr.includes(val) && arr.length >= 10) { toast.error("Maximum 10 items allowed"); return; }
    update({ [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] });
  };
  const addMed = () => {
    if (form.medications.length >= 10) { toast.error("Maximum 10 medications allowed"); return; }
    update({ medications: [...form.medications, { name: "", brand_name: "", dose: "", frequency: "" }] });
  };
  const updateMed = (i, patch) => {
    const m = [...form.medications]; m[i] = { ...m[i], ...patch }; update({ medications: m });
  };
  const removeMed = (i) => update({ medications: form.medications.filter((_, idx) => idx !== i) });

  const addCustom = (key, value, setter) => {
    const v = (value || "").trim();
    if (!v) return;
    if ((form[key] || []).some((x) => x.toLowerCase() === v.toLowerCase())) return;
    if ((form[key] || []).length >= 10) { toast.error("Maximum 10 items allowed"); return; }
    update({ [key]: [...(form[key] || []), v] });
    setter("");
  };
  const removeCustom = (key, i) => {
    const arr = [...(form[key] || [])];
    arr.splice(i, 1);
    update({ [key]: arr });
  };

  const isImperial = form.units === "imperial";
  const ftIn = cmToFtIn(form.height_cm);

  const save = async () => {
    setShowErrors(true);
    const error = ageError(form.age) || medicationsError(form.medications);
    setFormError(error);
    if (error) { toast.error(error); return; }
    setBusy(true);
    try {
      const payload = { ...form, age: Number(form.age), medications: cleanMedications(form.medications) };
      for (const [key, draft] of [["custom_allergies", newAllergy], ["excluded_ingredients", newExclude], ["preferred_ingredients", newInclude]]) {
        const items = payload[key] || [];
        const value = draft.trim();
        if (value && !items.some((item) => item.toLowerCase() === value.toLowerCase())) {
          if (items.length >= 10) { setFormError("Maximum 10 items allowed per list."); return; }
          payload[key] = [...items, value];
        }
      }
      const response = await api.put("/profile", payload);
      setForm(response.data);
      setNewAllergy(""); setNewExclude(""); setNewInclude("");
      toast.success("Profile updated");
    } catch (e) {
      setFormError(profileErrorMessage(e));
      toast.error(profileErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-8 fade-up">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Health profile</h1>
          <p className="text-slate-500 mt-2">Update your details and recommendations refresh instantly.</p>
        </div>
        <Button onClick={save} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save-btn">
          <Save className="w-4 h-4 mr-2" />
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>

      {formError && <p role="alert" className="mb-5 text-sm text-red-700" data-testid="profile-validation-error">{formError}</p>}
      <div className="space-y-6">
        <Card className="rounded-3xl border-emerald-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-semibold text-slate-900">Basics</h2>
            <div className="flex items-center gap-2 bg-emerald-50 rounded-full p-1">
              <button
                type="button"
                onClick={() => update({ units: "metric" })}
                data-testid="units-metric"
                className={`px-3 py-1 text-xs rounded-full transition-all ${form.units !== "imperial" ? "bg-white shadow font-semibold text-emerald-700" : "text-slate-500"}`}
              >
                <Ruler className="w-3 h-3 inline mr-1" /> cm / kg
              </button>
              <button
                type="button"
                onClick={() => update({ units: "imperial" })}
                data-testid="units-imperial"
                className={`px-3 py-1 text-xs rounded-full transition-all ${form.units === "imperial" ? "bg-white shadow font-semibold text-emerald-700" : "text-slate-500"}`}
              >
                ft / lb
              </button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Age</Label>
              <Input type="text" inputMode="numeric" className="rounded-xl mt-1.5" value={form.age}
                onChange={(e) => { if (/^\d*$/.test(e.target.value)) update({ age: normalizeAge(e.target.value) }); }}
                aria-invalid={showErrors && !!ageError(form.age)} data-testid="profile-age" />
            </div>
            <div>
              <Label>Sex</Label>
              <Select value={form.sex} onValueChange={(v) => update({ sex: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="profile-sex"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isImperial ? (
              <>
                <div>
                  <Label>Height (cm)</Label>
                  <Input type="number" className="rounded-xl mt-1.5" value={form.height_cm} onChange={(e) => update({ height_cm: +e.target.value })} data-testid="profile-height" />
                </div>
                <div>
                  <Label>Weight (kg)</Label>
                  <Input type="number" step="0.1" className="rounded-xl mt-1.5" value={form.weight_kg} onChange={(e) => update({ weight_kg: +e.target.value })} data-testid="profile-weight" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <Label>Height (ft / in)</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input
                      type="number" min="0" className="rounded-xl"
                      value={ftBuf}
                      onChange={(e) => setFtBuf(e.target.value)}
                      onBlur={() => update({ height_cm: ftInToCm(ftBuf || 0, inBuf || 0) })}
                      data-testid="profile-height-ft" placeholder="ft"
                    />
                    <Input
                      type="number" min="0" max="11" className="rounded-xl"
                      value={inBuf}
                      onChange={(e) => setInBuf(e.target.value)}
                      onBlur={() => update({ height_cm: ftInToCm(ftBuf || 0, inBuf || 0) })}
                      data-testid="profile-height-in" placeholder="in"
                    />
                  </div>
                </div>
                <div>
                  <Label>Weight (lb)</Label>
                  <Input
                    type="number" step="0.1" className="rounded-xl mt-1.5"
                    value={lbBuf}
                    onChange={(e) => setLbBuf(e.target.value)}
                    onBlur={() => {
                      const n = parseFloat(lbBuf);
                      if (!isNaN(n) && n > 0) update({ weight_kg: lbsToKg(n) });
                    }}
                    data-testid="profile-weight-lb"
                  />
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <Label>Activity</Label>
              <Select value={form.activity_level} onValueChange={(v) => update({ activity_level: v })}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="profile-activity"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">Sedentary</SelectItem>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="very_active">Very active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        <Card className="rounded-3xl border-emerald-100 p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-5">Health conditions</h2>
          <div className="flex flex-wrap gap-2">
            {ref.conditions.map((c) => {
              const on = form.conditions.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => toggle("conditions", c.id)} data-testid={`profile-condition-${c.id}`} className={`px-3 py-1.5 rounded-full text-sm border transition-all ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-emerald-100 bg-white text-slate-700"}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="rounded-3xl border-emerald-100 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-semibold text-slate-900">Medications</h2>
            <Button type="button" variant="outline" onClick={addMed} className="rounded-full" data-testid="profile-add-med">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {form.medications.length === 0 ? (
            <div className="text-center py-8 text-slate-400 text-sm border-2 border-dashed border-emerald-100 rounded-2xl">
              No medications listed.
            </div>
          ) : (
            <div className="space-y-2">
              {form.medications.map((m, i) => (
                <MedicationFields key={i} medication={m} index={i} prefix="profile"
                  onChange={(patch) => updateMed(i, patch)} onRemove={() => removeMed(i)} showErrors={showErrors} />
              ))}
            </div>
          )}
        </Card>

        <Card className="rounded-3xl border-emerald-100 p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-5">Allergies & diet</h2>
          <div className="mb-5">
            <Label className="mb-2 block">Common allergies</Label>
            <div className="flex flex-wrap gap-2">
              {ref.common_allergies.map((a) => {
                const on = form.allergies.includes(a);
                return (
                  <button key={a} type="button" onClick={() => toggle("allergies", a)} data-testid={`profile-allergy-${a.replace(/\W+/g, "-")}`} className={`px-3 py-1.5 rounded-full text-sm border ${on ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-100 bg-white"}`}>
                    {a}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-5">
            <Label className="mb-2 block">Other allergies (type & Enter)</Label>
            <div className="flex gap-2">
              <Input
                className="rounded-xl"
                value={newAllergy}
                onChange={(e) => setNewAllergy(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom("custom_allergies", newAllergy, setNewAllergy); }
                }}
                placeholder="e.g. Pineapple, Sulfites, Coconut"
                data-testid="profile-custom-allergy-input"
              />
              <Button type="button" variant="outline" className="rounded-full" onClick={() => addCustom("custom_allergies", newAllergy, setNewAllergy)} data-testid="profile-custom-allergy-add">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-custom-allergy-list">
              {(form.custom_allergies || []).map((a, i) => (
                <Badge key={i} variant="outline" className="rounded-full text-red-700 border-red-200 bg-red-50 pl-3 pr-1 py-1">
                  {a}
                  <button type="button" data-testid={`profile-custom-allergy-remove-${i}`} aria-label={`Remove ${a}`} onClick={() => removeCustom("custom_allergies", i)} className="ml-1 w-5 h-5 rounded-full hover:bg-red-100 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(form.custom_allergies || []).length === 0 && (
                <span className="text-xs text-slate-400">Anything you're personally allergic to — we'll strictly exclude these.</span>
              )}
            </div>
          </div>

          <div className="mb-5">
            <Label className="mb-2 block">Excluded ingredients</Label>
            <div className="flex gap-2">
              <Input
                className="rounded-xl"
                value={newExclude}
                onChange={(e) => setNewExclude(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom("excluded_ingredients", newExclude, setNewExclude); }
                }}
                placeholder="e.g. cilantro (add ingredients separately)"
                data-testid="profile-excluded-input"
              />
              <Button type="button" variant="outline" className="rounded-full" onClick={() => addCustom("excluded_ingredients", newExclude, setNewExclude)} data-testid="profile-excluded-add">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-excluded-list">
              {(form.excluded_ingredients || []).map((a, i) => (
                <Badge key={i} variant="outline" className="rounded-full text-slate-700 border-emerald-200 bg-emerald-50 pl-3 pr-1 py-1">
                  {a}
                  <button type="button" data-testid={`profile-excluded-remove-${i}`} aria-label={`Remove ${a}`} onClick={() => removeCustom("excluded_ingredients", i)} className="ml-1 w-5 h-5 rounded-full hover:bg-emerald-100 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(form.excluded_ingredients || []).length === 0 && (
                <span className="text-xs text-slate-400">Preferences (not allergies) — foods you just don't want to see.</span>
              )}
            </div>
          </div>

          <div className="mb-5">
            <Label className="mb-2 block">Preferred ingredients</Label>
            <div className="flex gap-2">
              <Input
                className="rounded-xl"
                value={newInclude || ""}
                onChange={(e) => setNewInclude(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); addCustom("preferred_ingredients", newInclude, setNewInclude); }
                }}
                placeholder="Foods to include in your meal plans (e.g. salmon, chickpeas)"
                data-testid="profile-preferred-input"
              />
              <Button type="button" variant="outline" className="rounded-full" onClick={() => addCustom("preferred_ingredients", newInclude, setNewInclude)} data-testid="profile-preferred-add">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="profile-preferred-list">
              {(form.preferred_ingredients || []).map((a, i) => (
                <Badge key={i} variant="outline" className="rounded-full text-emerald-700 border-emerald-200 bg-emerald-50 pl-3 pr-1 py-1">
                  {a}
                  <button type="button" data-testid={`profile-preferred-remove-${i}`} aria-label={`Remove ${a}`} onClick={() => removeCustom("preferred_ingredients", i)} className="ml-1 w-5 h-5 rounded-full hover:bg-emerald-100 flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              {(form.preferred_ingredients || []).length === 0 && (
                <span className="text-xs text-slate-400">The AI will try to feature these in your generated meal plans.</span>
              )}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Dietary preferences</Label>
            <div className="flex flex-wrap gap-2">
              {ref.dietary_preferences.map((d) => {
                const on = form.dietary_preferences.includes(d.id);
                return (
                  <button key={d.id} type="button" onClick={() => toggle("dietary_preferences", d.id)} data-testid={`profile-diet-${d.id}`} className={`px-3 py-1.5 rounded-full text-sm border ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-emerald-100 bg-white"}`}>
                    {d.name}
                  </button>
                );
              })}
            </div>
          </div>
        </Card>

        <Card className="rounded-3xl border-emerald-100 p-6">
          <h2 className="font-display font-semibold text-slate-900 mb-5">Goals & notes</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {ref.goals.map((g) => {
              const on = form.goals.includes(g.id);
              return (
                <button key={g.id} type="button" data-testid={`profile-goal-${g.id}`} onClick={() => toggle("goals", g.id)} className={`px-3 py-1.5 rounded-full text-sm border ${on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-emerald-100 bg-white"}`}>
                  {g.name}
                </button>
              );
            })}
          </div>
          <Textarea rows={3} className="rounded-xl" value={form.notes || ""} onChange={(e) => update({ notes: e.target.value })} placeholder="Anything else…" data-testid="profile-notes" />
        </Card>

        <div className="text-right">
          <Button onClick={save} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save-btn-bottom">
            <Save className="w-4 h-4 mr-2" />
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
